// src/RecommendedOrdersSection.jsx
import React, { useState } from 'react'
import {
  Stack,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Chip,
  SwipeableDrawer,
  Menu,
  MenuItem,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { motion } from 'framer-motion'
import { PANEL_SX, SHADOWS, hoverOnly } from './theme/tokens'
import { BOTTOM_SHEET_TRANSITION } from './theme/tokens';
import { formatDate } from './utils/format'

const MotionPaper = motion(Paper)

const cardVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.055, duration: 0.22, ease: 'easeOut' },
  }),
}

function getEarliestDate(orders = []) {
  const dates = orders
    .map((o) => new Date(o.reorder_date))
    .filter((d) => !isNaN(d))
  if (!dates.length) return '–'
  return formatDate(
    dates.reduce((m, d) => (d < m ? d : m), dates[0]),
    { placeholder: '–' },
  )
}

// 🔴 "Urgent" bedeutet in dieser App an drei Stellen drei Verschiedenes:
//   5 Tage   Kachel "Urgent reorders"  (`backend/dashboard.py REORDER_WINDOW_DAYS`)
//   7 Tage   dieser Chip
//  14 Tage   Listenzugehoerigkeit      (`purchase_orders/routes.py threshold_date`)
// Am 2026-09-19 vom Regel-16-Gate aufgedeckt. Die Zahlen sind NICHT
// vereinheitlicht -- das waere eine fachliche Entscheidung, keine Aufraeumung.
// Stattdessen traegt der Chip seine Frist jetzt im Text, damit niemand ihn mit
// der Zahl auf der Kachel gleichsetzt.
const DRINGEND_TAGE = 7

function countUrgent(orders = []) {
  const now = Date.now()
  return orders.filter((o) => {
    const d = new Date(o.reorder_date)
    return !isNaN(d) && (d.getTime() - now) / 86_400_000 <= DRINGEND_TAGE
  }).length
}

export default function RecommendedOrdersSection({
  groups,
  onPreview,
  onAccept,
  onDecline,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [sheet, setSheet] = useState({ open: false, group: null })
  const [menu, setMenu] = useState({ anchorEl: null, group: null })

  const openSheet = (e, group) => {
    e.stopPropagation()
    if (isMobile) {
      setSheet({ open: true, group })
    } else {
      setMenu({ anchorEl: e.currentTarget, group })
    }
  }
  const closeSheet = () => setSheet({ open: false, group: null })
  const closeMenu = () => setMenu({ anchorEl: null, group: null })

  const handleAccept = (group) => {
    onAccept?.(group.vendor_id, group.recommended_orders || [])
    closeSheet()
    closeMenu()
  }
  const handleDeclineAll = (group) => {
    onDecline?.(group.vendor_id, group.recommended_orders || [])
    closeSheet()
    closeMenu()
  }
  const handleDeclineItem = (group, sku) => {
    onDecline?.(group.vendor_id, [{ sku }])
    closeSheet()
    closeMenu()
  }

  if (!groups?.length) {
    return (
      <Typography align="center" sx={{ py: 4, color: 'text.secondary' }}>
        No recommended orders.
      </Typography>
    )
  }

  /* ── Desktop: flat rows inside a single outlined Paper ── */
  const desktopRows = (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {groups.map((group, i) => {
        const orders = group.recommended_orders || []
        const urgent = countUrgent(orders)
        return (
          <Box
            key={group.vendor_id}
            onClick={() => onPreview?.(group)}
            sx={{
              // \ud83d\udd34 F9 (UI-Durchgang 2, 2026-10-07): Raster statt Fliesstext.
              // Vorher lagen die Meta-Zeilen in einem `flex`-Streifen ohne feste
              // Spalten \u2014 \u201en SKUs" und \u201eReorder by \u2026" standen je nach Laenge des
              // Lieferantennamens an anderer x-Position. Die Bruch-Anteile sind
              // proportional, also inhaltsunabhaengig; die Spalte \u201eRank" ist
              // IMMER da und nur ihr Inhalt ist bedingt, sonst rutschte die
              // Aktionsspalte je Zeile.
              display: 'grid',
              // 🔴 Folgerunde 10 (2026-10-08): ZWEI Aenderungen, beide gemessen.
              //
              // (1) **Die Meta-Spalten duerfen nicht unter ihren Inhalt
              //     schrumpfen** — `minmax(min-content, …)` statt `minmax(0, …)`.
              //     Mit `minmax(0, …)` fiel die Datumsspalte bei 620 px auf
              //     **27 px** (gemessen), waehrend ihr laengstes Wort
              //     „03.07.2026" **56 px** braucht: `whiteSpace: nowrap` war dort
              //     schon weg (R15 W6), aber Umbruch hilft nicht, wenn die SPUR
              //     schmaler ist als das laengste Wort. Das `Paper` traegt
              //     `overflow: hidden`, das Datum war also abgeschnitten.
              //     Nur die Namensspalte darf weiter auf 0 — sie traegt `noWrap`
              //     und ellipsiert (`Stack` mit `minWidth: 0`).
              //
              // (2) **Unter `md` stehen die Meta-Zellen in einer ZWEITEN Zeile.**
              //     (1) allein genuegt nicht: die vier Meta-Spuren brauchen dann
              //     0 + ~40 + 56 + ~45 = ~141 px, die Aktionsspalte ist `auto`
              //     und damit ~120 px (Button „Accept" 64 · IconButton 30 ·
              //     Chevron 11 · 2×8 Abstand), die vier Spaltenabstaende 48 —
              //     zusammen ~309 px. Verfuegbar sind bei 620 px Viewport
              //     ~300 px (Rahmen mit Seitenleiste 240 → 380, abzueglich
              //     Seitenraender und `px: 2`): die Zeile liefe waagerecht
              //     ueber und das `overflow: hidden` schnitte die AKTIONEN ab.
              //     Zwei Zeilen loesen es ohne Inhaltsverlust — Zeile 1
              //     Name + Aktionen (~132 px), Zeile 2 die drei Meta (~125 px),
              //     beide weit unter der Verfuegbarkeit. Ab `md` bleibt die
              //     einzeilige Fassung (dort sind es ~580 px).
              //     ⚠️ Der Umschaltpunkt ist `md` (900 px), NICHT `sm`: die
              //     Kartenform greift erst unter 600 px (`down('sm')`), das Band
              //     600–899 px faehrt also die Zeile — und genau dort ist sie am
              //     schmalsten.
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr) auto auto',
                md: 'minmax(0, 1.7fr) minmax(min-content, 0.7fr) minmax(min-content, 1.2fr) minmax(min-content, 0.7fr) auto',
              },
              // Die Bereiche tragen dieselben Namen in beiden Fassungen — die
              // Zellen unten nennen nur ihren Namen und folgen der jeweils
              // gueltigen Vorlage. Kein `order`-Umbau: die DOM-Reihenfolge
              // (Name, SKUs, Datum, Rank, Aktionen) bleibt die Leserichtung.
              gridTemplateAreas: {
                xs: '"name name actions" "skus datum rank"',
                md: '"name skus datum rank actions"',
              },
              alignItems: 'center',
              px: 2,
              py: 1.25,
              gap: 1.5,
              cursor: 'pointer',
              borderTop: i > 0 ? '1px solid' : 'none',
              borderColor: 'divider',
              transition: 'background-color 0.15s ease',
              ...hoverOnly({ bgcolor: 'action.hover' }),
            }}
          >
            {/* Vendor name + urgent chip. Die EINZIGE Zelle, die unter ihre
                Inhaltsbreite schrumpfen darf — sie traegt `noWrap` (Ellipse). */}
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, gridArea: 'name' }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {group.vendor_name}
              </Typography>
              {urgent > 0 && (
                <Chip label={`${urgent} due \u2264${DRINGEND_TAGE}d`} size="small" color="warning" sx={{ height: 18, fontSize: 11, flexShrink: 0 }} />
              )}
            </Stack>

            {/* Meta */}
            <Typography variant="caption" color="text.secondary" noWrap sx={{ gridArea: 'skus' }}>
              {orders.length} SKU{orders.length !== 1 ? 's' : ''}
            </Typography>
            {/* 🔴 R15 W6 / R16 W2 (2026-10-08): KEIN `noWrap`. Mit
                `whiteSpace: nowrap` kann der Text nicht umbrechen; die Spur
                `minmax(0, 1.2fr)` schrumpft dann unter ihren Inhalt, und weil
                das `Paper` `overflow: hidden` traegt, wird das DATUM
                abgeschnitten („Reorder by 07.10.2…") — ausgerechnet im Band
                600–1000 px, das keine Abnahme misst. Umbruch ist hier die
                bessere Form: die Zeile waechst, statt zu kuerzen.
                🔴 Folgerunde 10: das allein genuegte NICHT. Ohne Umbruch-Verbot
                bricht der Text zwar um, aber die Spur war bei 620 px 27 px breit
                (gemessen) — schmaler als das laengste Wort „03.07.2026" (56 px).
                Deshalb traegt die Spur jetzt `minmax(min-content, …)` und kann
                nicht mehr unter ihr laengstes Wort fallen. */}
            <Typography variant="caption" color="text.secondary" sx={{ gridArea: 'datum' }}>
              Reorder by {getEarliestDate(orders)}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ gridArea: 'rank' }}>
              {group.vendor_category_order != null ? `Rank #${group.vendor_category_order}` : ''}
            </Typography>

            {/* Actions \u2014 die drei Ziele mit Abstand statt gedraengt */}
            <Stack direction="row" alignItems="center" spacing={1}
              onClick={(e) => e.stopPropagation()} sx={{ justifySelf: 'end', gridArea: 'actions' }}>
              <Button
                size="small"
                variant="outlined"
                sx={{ fontSize: 12 }}
                onClick={() => onAccept?.(group.vendor_id, orders)}
              >
                Accept
              </Button>
              <IconButton size="small" onClick={(e) => openSheet(e, group)}
                aria-label={`More actions for ${group.vendor_name}`} sx={{ color: 'text.secondary' }}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <ArrowForwardIosIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
            </Stack>
          </Box>
        )
      })}
    </Paper>
  )

  /* ── Mobile: stagger cards ── */
  const mobileCards = (
    <Stack spacing={1.5}>
      {groups.map((group, i) => {
        const orders = group.recommended_orders || []
        const urgent = countUrgent(orders)
        return (
          <MotionPaper
            key={group.vendor_id}
            variant="outlined"
            custom={i}
            initial="hidden"
            animate="visible"
            variants={cardVariants}
            sx={{
              ...PANEL_SX,
              cursor: 'pointer',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              ...hoverOnly({ transform: 'translateY(-1px)', boxShadow: SHADOWS.panelHover }),
            }}
            onClick={() => onPreview?.(group)}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" sx={{ mb: 0.25 }}>
                  <Typography variant="subtitle2" fontWeight={700} noWrap>
                    {group.vendor_name}
                  </Typography>
                  {urgent > 0 && (
                    <Chip label={`${urgent} due \u2264${DRINGEND_TAGE}d`} size="small" color="warning" sx={{ height: 20, fontSize: 11 }} />
                  )}
                </Stack>
                <Box sx={{
                  display: 'grid',
                  // 🔴 R16 W2 (2026-10-08): `minmax(0, 1fr)` statt `1fr`. `1fr`
                  //    ist `minmax(auto, 1fr)` — die mittlere Spur haette die
                  //    MINIMALBREITE ihres Inhalts als Untergrenze und wuerde
                  //    bei einem langen Datum breiter, statt umzubrechen; die
                  //    Karte liefe waagerecht ueber.
                  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                  gap: 1.5,
                  alignItems: 'baseline',
                }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {orders.length} SKU{orders.length !== 1 ? 's' : ''}
                  </Typography>
                  {/* Umbruch statt Ellipse — dieselbe Begruendung wie in der
                      Desktop-Zeile: ein abgeschnittenes DATUM ist schlechter
                      als ein umbrochenes Wort. */}
                  <Typography variant="caption" color="text.secondary">
                    Reorder by {getEarliestDate(orders)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {group.vendor_category_order != null ? `Rank #${group.vendor_category_order}` : ''}
                  </Typography>
                </Box>
              </Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton size="small" onClick={(e) => openSheet(e, group)}
                  aria-label={`More actions for ${group.vendor_name}`} sx={{ color: 'text.secondary' }}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
                <ArrowForwardIosIcon sx={{ fontSize: 11, color: 'text.disabled', flexShrink: 0 }} />
              </Stack>
            </Stack>
          </MotionPaper>
        )
      })}
    </Stack>
  )

  return (
    <>
      {isMobile ? mobileCards : desktopRows}

      {/* Desktop: popover menu */}
      <Menu
        anchorEl={menu.anchorEl}
        open={Boolean(menu.anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {menu.group && [
          <MenuItem key="accept" onClick={() => handleAccept(menu.group)}>
            Accept as draft
          </MenuItem>,
          <MenuItem key="decline" onClick={() => handleDeclineAll(menu.group)} sx={{ color: 'error.main' }}>
            Decline all
          </MenuItem>,
          ...(menu.group.recommended_orders || []).length > 0
            ? [
                <Divider key="div" />,
                ...(menu.group.recommended_orders || []).map((item) => (
                  <MenuItem
                    key={item.sku}
                    onClick={() => handleDeclineItem(menu.group, item.sku)}
                    sx={{ fontSize: 13 }}
                  >
                    Decline: {item.product_name || item.variant_name || item.sku}
                  </MenuItem>
                )),
              ]
            : [],
        ]}
      </Menu>

      {/* Mobile: bottom sheet */}
      <SwipeableDrawer
        anchor="bottom"
        open={sheet.open}
        onOpen={() => {}}
        onClose={closeSheet}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            borderRadius: '16px 16px 0 0',
            maxHeight: '80vh',
          },
        }}

        {...BOTTOM_SHEET_TRANSITION}
      >
        {sheet.group && (
          <Box sx={{ pt: 2, pb: 4 }}>
            <Box
              sx={{
                width: 36,
                height: 4,
                borderRadius: 99,
                bgcolor: 'divider',
                mx: 'auto',
                mb: 2,
              }}
            />
            <Typography variant="subtitle2" fontWeight={700} sx={{ px: 2, mb: 1 }}>
              {sheet.group.vendor_name}
            </Typography>
            <List disablePadding>
              <ListItemButton onClick={() => handleAccept(sheet.group)} sx={{ borderRadius: 1, mx: 1 }}>
                <ListItemText primary="Accept as draft" />
              </ListItemButton>
              <ListItemButton onClick={() => handleDeclineAll(sheet.group)} sx={{ borderRadius: 1, mx: 1 }}>
                <ListItemText primary="Decline all" primaryTypographyProps={{ color: 'error' }} />
              </ListItemButton>
              {(sheet.group.recommended_orders || []).length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ px: 3, display: 'block', mb: 0.5 }}>
                    Decline single item
                  </Typography>
                  {(sheet.group.recommended_orders || []).map((item) => (
                    <ListItemButton
                      key={item.sku}
                      onClick={() => handleDeclineItem(sheet.group, item.sku)}
                      sx={{ borderRadius: 1, mx: 1 }}
                    >
                      <ListItemText
                        primary={item.product_name || item.variant_name || item.sku}
                        secondary={item.sku}
                        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItemButton>
                  ))}
                </>
              )}
            </List>
          </Box>
        )}
      </SwipeableDrawer>
    </>
  )
}
